import React from "react";
import { Box, Button, Card, Chip, IconButton, Typography } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import styles from "./complianceReporting.module.scss";
import { useNavigate } from "react-router-dom";

const frameworks = [
  {
    name: "ISO 27001",
    id: 1,
    completed: 114,
    total: 121,
    percent: 94,
    color: "#0FA3B1",
  },
  {
    name: "IEC 62443",
    id: 2,

    completed: 67,
    total: 77,
    percent: 87,
    color: "#F4A261",
  },
  {
    name: "NIST CSF",

    completed: 98,
    total: 108,
    percent: 91,
    color: "#00B894",
  },
  { name: "HIPAA", completed: 42, total: 54, percent: 78, color: "#EF4444" },
];

const reports = [
  {
    name: "ISO 27001 Compliance Report",
    id: 1,

    framework: "ISO 27001",
    period: "Q4 2024",
    score: 94,
    generated: "2024-12-01",
    status: "Approved",
  },
  {
    name: "IEC 62443 Assessment",
    id: 2,

    framework: "IEC 62443",
    period: "Q4 2024",
    score: 87,
    generated: "2024-11-28",
    status: "Pending Review",
  },
  {
    name: "NIST CSF Security Review",
    id: 3,

    framework: "NIST CSF",
    period: "Q3 2024",
    score: 91,
    generated: "2024-10-15",
    status: "Approved",
  },
  {
    name: "HIPAA Compliance Audit",
    id: 4,

    framework: "HIPAA",
    period: "Q3 2024",
    score: 78,
    generated: "2024-09-30",
    status: "Approved",
  },
];

const statusTone = {
  Approved: styles.statusApproved,
  "Pending Review": styles.statusPending,
};

const ComplianceReporting = () => {
  const navigate = useNavigate();

  const handleNavigate = (id) => {
    navigate(`/compliance-reports/${id}`);
  };

  return (
    <Box className={styles.page}>
      <Box className={styles.header}>
        <div>
          <Typography className={styles.title} fontWeight={600}>
            Compliance & Reporting
          </Typography>
          <Typography className={styles.subtitle}>
            Regulatory compliance tracking and evidence generation
          </Typography>
        </div>
        <Button
          variant="contained"
          className={styles.primaryButton}
          startIcon={<InsertDriveFileOutlinedIcon />}
        >
          Generate New Report
        </Button>
      </Box>

      <Box className={styles.statGrid}>
        {frameworks.map((fw) => (
          <Card key={fw.name} className={styles.statCard}>
            <div className={styles.statTitleRow}>
              <div>
                <div className={styles.statName}>{fw.name}</div>
                <div className={styles.statSubtitle}>
                  {fw.completed}/{fw.total} controls
                </div>
              </div>
            </div>
            <div className={styles.statValue} style={{ color: fw.color }}>
              {fw.percent}%
            </div>
            <div className={styles.progressBar}>
              <span
                className={styles.progressFill}
                style={{ width: `${fw.percent}%`, background: fw.color }}
              />
            </div>
          </Card>
        ))}
      </Box>

      <Card className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <Typography className={styles.tableTitle}>
            Generated Reports
          </Typography>
        </div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Report Name</th>
              <th>Framework</th>
              <th>Period</th>
              <th>Score</th>
              <th>Generated</th>
              <th>Status</th>
              <th className={styles.actionsHead}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr key={report.name}>
                <td className={styles.reportNameCell}>
                  <InsertDriveFileOutlinedIcon className={styles.fileIcon} />
                  <div className={styles.reportName}>{report.name}</div>
                </td>
                <td>
                  <Chip
                    label={report.framework}
                    size="small"
                    className={styles.frameworkChip}
                  />
                </td>
                <td className={styles.periodCell}>
                  <span className={styles.periodIcon}>🗓️</span>
                  {report.period}
                </td>
                <td className={styles.scoreCell} data-score={report.score}>
                  <span
                    className={`${styles.scoreValue} ${
                      report.score >= 90
                        ? styles.scoreGreen
                        : report.score >= 80
                        ? styles.scoreOrange
                        : styles.scoreRed
                    }`}
                  >
                    {report.score}%
                  </span>
                </td>
                <td className={styles.generatedCell}>{report.generated}</td>
                <td>
                  <span
                    className={`${styles.statusPill} ${
                      statusTone[report.status] || ""
                    }`}
                  >
                    {report.status}
                  </span>
                </td>
                <td className={styles.actionsCell}>
                  <IconButton
                    size="small"
                    className={styles.iconButton}
                    onClick={() => handleNavigate(report.id)}
                  >
                    <RemoveRedEyeOutlinedIcon />
                  </IconButton>
                  <IconButton size="small" className={styles.iconButton}>
                    <DownloadOutlinedIcon />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Box>
  );
};

export default ComplianceReporting;
